
import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";
import { CreateElement } from "vue";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import ProvisioningMixin from "@/mixins/provisioningMixin";
import CurrenciesStore from "@/mixins/constants/currenciesStore";
import BrandCurrencyId from "@/mixins/brandCurrencyId";
import BrandsStore from "@/mixins/brandsStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ProductTypes } from "@/data/enums/products";
import { QUERY_PARAMS } from "@/data/constants";

// Types
import { IState } from "@/store";
import {
  IProductProviderData,
  IProductProviderMethods
} from "@/models/providers/products";
import { IProduct } from "@/models/products";

@Component({
  computed: {
    ...mapState({
      productScope(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/scope`](this.productId);
      },
      product(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE,
          scope: this.productScope
        });
      },
      productWarningMessages(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/warningMessages`](
          this.productId
        );
      }
    })
  }
})
export default class ProductProvider extends Mixins(
  RequestPromises,
  CurrenciesStore,
  ProvisioningMixin,
  BrandCurrencyId,
  BrandsStore
) {
  // Map State
  public product!: IProduct;
  public productScope!: string;
  public productWarningMessages!: string;

  // Provide
  @Provide("productProviderData")
  public productProviderData(): IProductProviderData {
    return {
      product: this.product,
      isProcessing: this.isProcessing,
      i18n: this.i18n,
      isProduct: this.isProduct,
      isStaged: this.isStaged
    };
  }

  @Provide("productProviderMethods")
  public get productProviderMethods(): IProductProviderMethods {
    return {
      startProductDuplication: this.startProductDuplication,
      copyToClipboard: this.copyToClipboard,
      openDeleteProductConfirmation: this.openDeleteProductConfirmation
    };
  }

  // Props
  @Prop({ required: true })
  public productId!: IProduct["id"];

  // Data
  public isLoading = true;
  public isReloading = false;
  public requestError = false;
  public isProcessing = {};

  public savedProductScope = "";

  public async created() {
    this.loadData();
  }

  public async loadData() {
    try {
      this.savedProductScope = this.productScope;

      await Promise.all([
        this.getProductData(this.isReloading),
        this.getCurrencies()
      ]);

      this.checkProductBrand();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProductData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/get`, {
      id: this.productId,
      scope: this.savedProductScope,
      vm: this,
      ignoreStored
    });
  }

  public checkProductBrand() {
    if (this.brandId !== this.$_.get(this.product, "brand_id")) {
      this.$store.dispatch("toast/error", {
        message: this.$t("_.product_from_different_brand")
      });

      this.$router.push({
        name: this.isOption
          ? "adminCatalogueOptions"
          : this.isAttribute
          ? "adminCatalogueAttributes"
          : "adminCatalogueProducts"
      });
    }
  }

  public copyToClipboard() {
    this.$copyText(this.generateLink()).then(() => {
      this.$store.dispatch("toast/show", {
        message: this.$t("_.copied_to_clipboard")
      });
    });
  }

  public generateLink() {
    const defaultDomainOrigin = this.$_.get(
      this.$_.find(this.$_.get(this.brand, "oauth_clients", []), [
        "default",
        true
      ]),
      "origin",
      ""
    );
    return `${defaultDomainOrigin}/order/product?${
      QUERY_PARAMS.CATEGORY_ID
    }=${this.$_.get(this.product, "category_id")}&${
      QUERY_PARAMS.PRODUCT_ID
    }=${this.$_.get(this.product, "id")}`;
  }

  public async openDeleteProductConfirmation() {
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.i18n.deleteSentence
        },
        events: {
          confirmed: async () => {
            this.$set(this.isProcessing, "delete", true);
            try {
              await this.deleteProduct();
              this.$store.dispatch("toast/show", {
                message: this.i18n.productDeletedMsg
              });

              this.$router.push({
                name: this.isOption
                  ? "adminCatalogueOptions"
                  : this.isAttribute
                  ? "adminCatalogueAttributes"
                  : "adminCatalogueProducts"
              });
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            }
            this.$delete(this.isProcessing, "delete");
            confirm.close();
          }
        }
      }
    });
  }

  public deleteProduct() {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/remove`, {
      id: this.productId
    });
  }

  public async startProductDuplication() {
    this.$set(this.isProcessing, "duplicate", true);
    try {
      const result = await this.duplicateProduct();
      this.$store.dispatch("toast/show", {
        message: this.i18n.productDuplicatedMsg
      });

      this.$store.dispatch("data/bin", {
        storeModule: DataModules.CATALOGUE,
        scope: this.savedProductScope,
        vm: this
      });

      this.$router.push({
        name: this.isOption
          ? "adminCatalogueOption"
          : this.isAttribute
          ? "adminCatalogueAttribute"
          : "adminCatalogueProduct",
        params: {
          productId: result.id
        }
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }

    this.$delete(this.isProcessing, "duplicate");
  }

  public duplicateProduct() {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/duplicate`, {
      id: this.productId,
      unitId: this.$_.get(this.product, "unit_id", null)
    });
  }

  // Computed data
  public get productType() {
    return this.$_.get(this.product, "product_type", "");
  }

  public get isOption() {
    return this.productType === ProductTypes.PRODUCT_OPTION;
  }

  public get isAttribute() {
    return this.productType === ProductTypes.PRODUCT_ATTRIBUTE;
  }

  public get isProduct() {
    return (
      this.productType === ProductTypes.SINGLE_PRODUCT ||
      this.productType === ProductTypes.PRODUCT_BUNDLE
    );
  }

  public get canShowAutomation() {
    return (
      this.isProduct && this.$_.get(this.product, "billing_cycle_months", 0) > 0
    );
  }

  public get canShowRecommendations() {
    return this.productType === ProductTypes.SINGLE_PRODUCT;
  }

  public get canShowUpgradesDowngrades() {
    return (
      !!this.product.billing_cycle_months &&
      this.productType === ProductTypes.SINGLE_PRODUCT
    );
  }

  public get isStaged() {
    return !!this.$_.get(this.product, "staged_import");
  }

  public get i18n() {
    return {
      manage: this.$t("_action.manage_object", {
        object: this.productWithType.toString().toLowerCase()
      }),
      duplicate: this.$t("_action.duplicate_object", {
        object: this.productWithType.toString().toLowerCase()
      }),
      deleteSentence: this.$t("_sentence.delete_confirmation", {
        object: this.productWithType.toString().toLowerCase()
      }),
      productDeletedMsg: this.$t("_.object_successfully_removed", {
        object: this.productWithType
      }),
      productDuplicatedMsg: this.$t("_.object_successfully_duplicated", {
        object: this.productWithType
      }),
      copyOrderUrl: this.$t("_.copy_order_url")
    };
  }

  public get productWithType() {
    return this.isOption
      ? this.$t("_.product_option")
      : this.isAttribute
      ? this.$t("_.product_attribute")
      : this.$t("_.product");
  }

  // Used by router menu
  public get routeHasError() {
    return this.productWarningMessages === null
      ? false
      : !this.$_.isEmpty(this.productWarningMessages);
  }

  // Used by router menu
  public get hasMissingPrices() {
    return (
      !this.$_.get(this.product, "prices", []).length ||
      this.$_.filter(this.$_.get(this.product, "prices", []), price => {
        return (
          this.usedTerms.includes(price.billing_cycle_months) &&
          this.usedCurrencies.includes(price.currency_id) &&
          this.defaultPriceListId === price.pricelist_id
        );
      }).length !==
        this.usedTerms.length * this.usedCurrencies.length
    );
  }

  public get usedCurrencies() {
    return this.$_.uniq(
      this.$_.concat(
        [this.brandCurrencyId],
        this.$_.map(
          this.$_.uniqBy(
            this.$_.get(this.product, "prices", []),
            "currency_id"
          ),
          price => price.currency_id
        )
      )
    );
  }

  public get usedTerms() {
    return this.$_.map(
      this.$_.uniqBy(
        this.$_.get(this.product, "prices", []),
        "billing_cycle_months"
      ),
      price => price.billing_cycle_months
    );
  }

  // Render function
  public render(createElement: CreateElement) {
    if (this.isLoading)
      return createElement("is-loading", {
        props: { if: true },
        attrs: { isAbsolute: true }
      });

    if (this.requestError)
      return createElement("has-request-error", {
        props: {
          hasError: true,
          isLoading: this.isReloading
        },
        attrs: { isAbsolute: true },
        on: {
          retry: this.reloadData
        }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $productData: this.productProviderData(),
        $productMethods: this.productProviderMethods
      })
    );
  }

  // Before destroy
  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE,
      scope: this.savedProductScope,
      vm: this
    });
  }
}
